import { Checkbox, ICheckboxProps, TextField } from '@fluentui/react';
import { ContentSection } from '../../../components/LayoutElements';
import { useConfigValuesState } from '../../../hooks/useConfigValue';
import { TimeSheetValidation } from '../../../api/generated/data-contracts';
import React, { useCallback, useState } from 'react';

export const ValidationConfig = () => {
    return (
        <ContentSection>
            <div style={{ display: 'flex', flexDirection: 'column' }}>
                <RulesCheckboxes />
            </div>
        </ContentSection>
    );
};

const RulesCheckboxes = () => {
    const {
        initialData: {
            configuration: { timeSheetValidation },
        },
        setConfigValue,
    } = useConfigValuesState();

    const setRule = useCallback(
        <K extends keyof TimeSheetValidation>(key: K, value: TimeSheetValidation[K]) => {
            setConfigValue(config => {
                return {
                    ...config,
                    timeSheetValidation: {
                        ...config.timeSheetValidation,
                        [key]: value,
                    },
                };
            });
        },
        [setConfigValue],
    );

    return (
        <>
            <Checkbox
                styles={checkBoxStyles}
                label="Timesheet must not contain cells with errors"
                defaultChecked={timeSheetValidation.noCellErrors}
                onChange={(e, checked) => setRule('noCellErrors', checked)}
            />
            {/* <Checkbox
                styles={checkBoxStyles}
                label="Limit the max number of hours a user is able to timesheet per day"
                defaultChecked={timeSheetValidation.maxHoursPerDayEnabled}
                onChange={(e, checked) => setRule('maxHoursPerDayEnabled', checked)}
            /> */}
            <InputContainer border={timeSheetValidation.maxHoursPerPeriodEnabled}>
                <Checkbox
                    // styles={checkBoxStyles}
                    label="Limit the max number of hours a user is able to timesheet per period"
                    defaultChecked={timeSheetValidation.maxHoursPerPeriodEnabled}
                    onChange={(e, checked) => setRule('maxHoursPerPeriodEnabled', checked)}
                />
                {timeSheetValidation.maxHoursPerPeriodEnabled && (
                    <TextField
                        //
                        label="Max hours per period"
                        defaultValue={String(timeSheetValidation.maxHoursAllowedPerPeriod)}
                        onBlur={e => setRule('maxHoursAllowedPerPeriod', Number(e.target.value))}
                        type="number"
                    />
                )}
            </InputContainer>
            <Checkbox
                styles={checkBoxStyles}
                label="Only allow user to time register within earliest and latest available dates"
                defaultChecked={timeSheetValidation.onlyAllowWithinEmployment}
                onChange={(e, checked) => setRule('onlyAllowWithinEmployment', checked)}
            />
            <Checkbox
                styles={checkBoxStyles}
                label="Ensure all time entries have a worktype (only relevent if worktypes are enabled)"
                defaultChecked={timeSheetValidation.timeEntriesMustHaveWorkType}
                onChange={(e, checked) => setRule('timeEntriesMustHaveWorkType', checked)}
            />
            <InputContainer border={timeSheetValidation.useFlowValidation}>
                <Checkbox
                    label="Configure additional validation rules in Power Automate"
                    defaultChecked={timeSheetValidation.useFlowValidation}
                    onChange={(e, checked) => setRule('useFlowValidation', checked)}
                />
                {timeSheetValidation.useFlowValidation && (
                    <TextField
                        //
                        label="Power Automate URL"
                        defaultValue={timeSheetValidation.flowUrl}
                        onBlur={e => setRule('flowUrl', e.target.value)}
                    />
                )}
            </InputContainer>

            <InputContainer border={timeSheetValidation.minPercentPerDayEnabled}>
                <Checkbox
                    styles={{ root: { margin: 0 } }}
                    label="MinPercentPerDayEnabled"
                    defaultChecked={timeSheetValidation.minPercentPerDayEnabled}
                    onChange={(e, checked) => setRule('minPercentPerDayEnabled', checked)}
                />
                {timeSheetValidation.minPercentPerDayEnabled && (
                    <PercentField
                        onBlur={e => setRule('minPercentPerDay', Number(e.target.value))}
                        label="Min percent per day"
                        defaultValue={timeSheetValidation.minPercentPerDay}
                    />
                )}
            </InputContainer>
            <InputContainer border={timeSheetValidation.maxPercentOfCapacityEnabled}>
                <Checkbox
                    label="MaxPercentOfCapacityEnabled"
                    defaultChecked={timeSheetValidation.maxPercentOfCapacityEnabled}
                    onChange={(e, checked) => setRule('maxPercentOfCapacityEnabled', checked)}
                />
                {timeSheetValidation.maxPercentOfCapacityEnabled && (
                    <PercentField
                        onBlur={e => setRule('maxPercentOfCapacity', Number(e.target.value))}
                        label="Max percent of capacity"
                        defaultValue={timeSheetValidation.maxPercentOfCapacity}
                    />
                )}
            </InputContainer>

            <Checkbox
                styles={checkBoxStyles}
                // label="Timesheet must contain entries before being submitted"
                label="Allow empty timesheets to submitted"
                defaultChecked={timeSheetValidation.emptyTimeSheetAllowed}
                onChange={(e, checked) => setRule('emptyTimeSheetAllowed', checked)}
            />
        </>
    );
};

const PercentField = ({
    defaultValue,
    label,
    onBlur,
}: {
    defaultValue: number;
    label?: string;
    onBlur?: React.FocusEventHandler<HTMLInputElement | HTMLTextAreaElement>;
}) => {
    const [value, setValue] = useState(defaultValue);
    const onChange = useCallback((event: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>, val?: string) => {
        const newVal = Number(val);
        if (newVal <= 100 && newVal >= 0) {
            setValue(newVal);
        }
    }, []);
    return <TextField label={label} suffix="%" type="number" min={'0'} max={'100'} value={String(value)} onChange={onChange} onBlur={onBlur} />;
};

const checkBoxStyles: ICheckboxProps['styles'] = { root: { margin: 10, border: '1px solid rgba(0,0,0,0)', marginTop: 10 } };

const InputContainer = ({ children, border }: { children: React.ReactNode; border?: boolean }) => {
    return (
        <div
            style={{
                border: border ? '1px solid rgba(255,255,255,0.5)' : '1px solid rgba(255,255,255,0)',
                transition: 'all 300ms',
                padding: 10,
                margin: 0,
            }}
        >
            {children}
        </div>
    );
};
